import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';

import { SignalService } from '@services/administration/machine/signal.service';
import { StatusEventSignalService } from '@services/administration/machine/status-event/status-event-signal.service';
import { SortingService } from '@services/administration/sorting/sorting.service';
import { UtilsService } from '@services/utils/utils.service';

import { ConfirmationDialogComponent } from '@shared/confirmation-dialog/confirmation-dialog.component';
import { ModalDialogDismissOption } from '@shared/confirmation-dialog/modal-dialog-dismiss-option';

import { SignalField } from '@models/adminstration/machine/signal-field';
import { Signal } from '@models/dashboard/signal';
import { StatusEventDetails } from '@models/machine/status-event-signal/status-event-details';

import { SIGNAL_TYPES } from '@constants/administration/machine/signal-type';
import {
  SIGNAL_NAME_REGEX,
  SIGNED_FLOAT_NUMBERS_ONLY_REGEX,
  UNSIGNED_INTEGER_NUMBERS_ONLY_REGEX
} from '@constants/reg-exp';
import { ModalService } from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-signal-edit-modal',
    templateUrl: './signal-edit-modal.component.html',
    styleUrls: ['./signal-edit-modal.component.scss'],
    standalone: false
})
export class SignalEditModalComponent implements OnInit {

  @Input()
  signal: Signal = new Signal('', '', '', '');

  @Input()
  machineId: number = -1;

  @Input()
  allSignals: Signal[] = [];

  @Input()
  public errorDetails: Map<string, StatusEventDetails> = new Map<string, StatusEventDetails>();

  @Input()
  public applyToMachinesOfSameTag: boolean = false;

  @Input()
  public isCommandModeDisabled: boolean = true;

  @Input()
  public applyToMachinesOfSameTagMessage: string = '';

  @Input()
  public isHigherThanAdmin: boolean = false;

  isNewSignal: boolean = false;
  originalSignalScale: number = 0;
  isLoadingTables: boolean = false;
  isLoadingSignals: boolean = false;
  submitted: boolean = false;
  signalNameUsed: boolean = false;
  signalLabelUsed: boolean = false;
  tables: string[] = new Array<string>();
  signals: SignalField[] = new Array<SignalField>();
  signalNames: string[] = new Array<string>();
  signalTypes = Object.values(SIGNAL_TYPES);
  translatedSignalTypes: any[] = new Array<any>();
  signalType: string = SIGNAL_TYPES.DEFAULT;
  selectedType: string = '';
  selectedSignal: string = '';
  DEFAULT_COLOR = '#CCC';

  signalForm: UntypedFormGroup = new UntypedFormGroup({});

  public errorSignalType: SIGNAL_TYPES = SIGNAL_TYPES.ERROR_WORD;
  public isLoadingErrorSignalDetails: boolean = false;

  constructor(
      private signalService: SignalService,
      private activeModal: NgbActiveModal,
      private sortingService: SortingService,
      private translate: TranslateService,
      private modalService: ModalService,
      private statusEventSignalService: StatusEventSignalService,
      private utilsService: UtilsService) {
  }

  get formControls() {
    return this.signalForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return !this.isHigherThanAdmin || (this.submitted ? this.signalForm.invalid : this.submitted && this.signalForm.invalid);
  };

  ngOnInit(): void {
    this.signal = this.utilsService.getObjectDeepCopy(this.signal);
    if (!this.signal || this.signal.id <= 0) {
      this.isNewSignal = true;
      this.originalSignalScale = 1;
      this.signal.id = -1;
      this.signal.isErrorWord = false;
      this.signal.isByteArray = false;
    } else {
      this.originalSignalScale = this.signal.scale;
      this.getErrorSignalDetails();
    }
    if (!this.signal.signalColor) {
      this.signal.signalColor = this.DEFAULT_COLOR;
    }
    if (this.signal.signal) {
      this.selectedSignal = this.signal.signal;
    }
    this.setTranslatedTypes();
    this.setSignalType();
    this.setPossibleTables();
    if (this.signal.table) {
      this.onChangeTable(this.signal.table);
    }
    this.generateSignalForm(this.signal);
  }

  onChangeTable(table: any) {
    this.signal.table = table;
    this.setSignalStatus();
    this.setPossibleSignals(table);
    this.signalForm.controls.signal?.setValue('');
  }

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel() {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSignalChange(selected: any) {
    this.selectedSignal = selected;
    this.signal.signal = selected;
    for (const signal of this.signals) {
      if (signal.name === this.selectedSignal) {
        this.setDisabledStatusForInput(signal.type, 'signalPrecision');
        this.setDisabledStatusForInput(signal.type, 'signalUnit');
        this.setDisabledStatusForInput(signal.type, 'signalScale');
        this.signal.type = signal.type;
        this.signal.mode = signal.mode;
        return;
      }
    }
  }

  onTypeChange(selected: any) {
    this.selectedType = selected.key;
    switch (this.selectedType) {
      case SIGNAL_TYPES.ERROR_WORD: {
        this.signal.isErrorWord = true;
        this.signal.isByteArray = false;
        break;
      }
      case SIGNAL_TYPES.BYTE_ARRAY: {
        this.signal.isErrorWord = false;
        this.signal.isByteArray = true;
        break;
      }
      case SIGNAL_TYPES.DEFAULT: {
        this.signal.isErrorWord = false;
        this.signal.isByteArray = false;
        break;
      }
    }
  }

  onSave() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.signalForm.invalid || this.signalLabelUsed || this.signalNameUsed) {
      return;
    }
    if (this.shouldShowScaleChangingWarning()) {
      this.createScaleChangeWarningModal(this.signal.usedInTriggers);
    }
    this.updateSignalProperties();
    this.activeModal.close(this.signal);
  }

  signalNameChanged() {
    this.signal.name = this.signalForm.controls.signalName.value;
    this.signalForm.controls.signalLabel.setValue(this.signalForm.controls.signalName.value);
    this.signalLabelChanged();
    this.signal.label = this.signal.name;
    this.signalNameUsed = this.allSignals.some(signal => signal.name.toLowerCase() === this.signal.name.toLowerCase());
  }

  signalLabelChanged() {
    this.signal.label = this.signalForm.controls.signalLabel.value;
    this.signalLabelUsed = this.signalService.signalLabelAlreadyUsed(this.allSignals, this.signal);
  }

  getSignalIsUsedInTriggers(): boolean {
    return !!this.signal.usedInTriggers;
  }

  getSignalIsDisabled(): boolean {
    return this.signal.table === '' || this.signal.usedInTriggers || this.isLoadingSignals || this.signalNames.length === 0 || !this.isHigherThanAdmin;
  }

  setSignalType(): void {
    if (this.signal.isErrorWord) {
      this.signalType = SIGNAL_TYPES.ERROR_WORD;
    } else if (this.signal.isByteArray) {
      this.signalType = SIGNAL_TYPES.BYTE_ARRAY;
    } else {
      this.signalType = SIGNAL_TYPES.DEFAULT;
    }
  }

  displaySignalDetails(): boolean {
    return this.signal.type !== 'STRING' && this.signal.type !== 'BOOL' && this.signal.type !== 'BOOLEAN';
  }

  onSelectedColor(selectedColor: string): void {
    this.signal.signalColor = selectedColor;
  }

  setSignals(tableSignals: SignalField[], tableName: string): void {
    this.signals = [];
    this.signalNames = [];
    tableSignals.forEach(tableSignal => {
      let signalExists = false;
      const existingTableSignals = this.allSignals.filter(m => m.table === tableName);
      if (existingTableSignals) {
        existingTableSignals.forEach((existingTableSignal: Signal) => {
          if (existingTableSignal.signal === tableSignal.name && existingTableSignal.id !== this.signal.id) {
            signalExists = true;
          }
        });
      }
      if (!signalExists) {
        this.signals.push(tableSignal);
        this.signalNames = [...this.signalNames, tableSignal.name];
      }
    });

    this.signalNames.sort((a, b) => this.sortingService.sortStrings(a, b));
  }

  signalScaleChanged(): void {
    this.signal.scale = this.signalForm.controls.signalScale.value;
  }

  signalUnitChanged(): void {
    this.signal.unit = this.signalForm.controls.signalUnit.value;
  }

  signalPrecisionChanged(): void {
    this.signal.decimals = this.signalForm.controls.signalPrecision.value;
  }

  private setSignalStatus(): void {
    if (this.formControls.signal) {
      const disableSignal = this.getSignalIsDisabled();
      if (disableSignal) {
        this.formControls.signal.disable();
      } else {
        this.formControls.signal.enable();
      }
    }
  }

  private setPossibleTables(): void {
    this.isLoadingTables = true;
    this.signalService.listTablesForMachine(this.machineId).subscribe((result) => {
      this.tables = result;
      this.isLoadingTables = false;
    }, (error) => {
      console.error('Signal Modal. Error loading the list of possible tables', JSON.stringify(error));
      this.isLoadingTables = false;
    });
  }

  private getErrorSignalDetails(): void {
    this.isLoadingErrorSignalDetails = true;
    if (!this.signal.isErrorWord) {
      this.isLoadingErrorSignalDetails = false;
      return;
    }
    this.signalService.getStatusEventSignalDetails(this.machineId, this.signal.id).subscribe((result) => {
      const signalClone = cloneDeep(this.signal);
      signalClone.statusEventsDetails = this.statusEventSignalService.convertObjectToStatusEventDetails(result);
      this.signal = signalClone;
      this.isLoadingErrorSignalDetails = false;
    }, (error) => {
      this.isLoadingErrorSignalDetails = false;
      console.error('Signal Modal. Error loading the error event details', JSON.stringify(error));
    });
  }

  private setPossibleSignals(tableName: string): void {
    this.isLoadingSignals = true;
    this.signals = [];
    this.signalNames = [];
    this.signalService.listSignalsForTable(this.machineId, tableName).subscribe((result) => {
      this.setSignals(result, tableName);
      this.isLoadingSignals = false;
      this.setSignalStatus();
    }, (error) => {
      console.error('Signal Modal. Error loading the list of possible signals', JSON.stringify(error));
      this.isLoadingSignals = false;
      this.setSignalStatus();
    });
  }

  private generateSignalForm(signal: Signal) {
    const tableIsDisabled = this.getSignalIsUsedInTriggers();
    const signalIsDisabled = this.getSignalIsDisabled();
    const signalNameIsDisabled = !this.isNewSignal;
    this.signalForm = new UntypedFormGroup({
      table: new UntypedFormControl({value: signal.table, disabled: tableIsDisabled}, [Validators.required]),
      signal: new UntypedFormControl({value: signal.signal, disabled: signalIsDisabled}, [Validators.required]),
      signalName: new UntypedFormControl({
        value: signal.name,
        disabled: signalNameIsDisabled
      }, [Validators.required, Validators.pattern(SIGNAL_NAME_REGEX)]),
      signalLabel: new UntypedFormControl(signal.label, [Validators.required]),
      signalScale: new UntypedFormControl(signal.scale, [Validators.pattern(SIGNED_FLOAT_NUMBERS_ONLY_REGEX)]),
      signalUnit: new UntypedFormControl(signal.unit),
      minWritableValue: new UntypedFormControl({value: signal.minWritableValue, disabled: !signal.writable}),
      maxWritableValue: new UntypedFormControl({value: signal.maxWritableValue, disabled: !signal.writable}),
      writable: new UntypedFormControl(signal.writable),
      signalPrecision: new UntypedFormControl(signal.decimals, [Validators.pattern(UNSIGNED_INTEGER_NUMBERS_ONLY_REGEX), Validators.min(0), Validators.max(9)]),
    });

    this.signalForm.controls.writable.valueChanges.subscribe({
      next: value => {
        if (value) {
          this.signalForm.get('minWritableValue')?.enable();
          this.signalForm.get('maxWritableValue')?.enable();
        } else {
          this.signalForm.get('minWritableValue')?.disable();
          this.signalForm.get('maxWritableValue')?.disable();
          this.signalForm.get('minWritableValue')?.setValue(null);
          this.signalForm.get('maxWritableValue')?.setValue(null);
        }
      }
    });

    this.signalForm.controls.minWritableValue.valueChanges.subscribe({
      next: value => {
        const maxControl = this.signalForm.get('maxWritableValue');
        if (maxControl) {
          maxControl.setValidators([Validators.min(value)]);
          maxControl.updateValueAndValidity();
        }
      }
    });

    this.signalForm.controls.maxWritableValue.valueChanges.subscribe({
      next: _ => {
        this.signalForm.get('maxWritableValue')?.markAsTouched();
      }
    });

    if (!this.isHigherThanAdmin) {
      this.signalForm.disable();
    }
  }

  private updateSignalProperties() {
    this.signal.table = this.formControls.table.value;
    this.signal.signal = this.formControls.signal.value;
    this.signal.name = this.formControls.signalName.value;
    this.signal.label = this.formControls.signalLabel.value;
    this.signal.writable = this.formControls.writable.value;
    this.signal.parentId = this.machineId;

    if (this.displaySignalDetails()) {
      this.signal.scale = this.formControls.signalScale.value;
      this.signal.unit = this.formControls.signalUnit.value;
      this.signal.minWritableValue = this.formControls.minWritableValue.value;
      this.signal.maxWritableValue = this.formControls.maxWritableValue.value;
      if (this.formControls.signalPrecision.value) {
        this.signal.decimals = this.formControls.signalPrecision.value;
      } else {
        this.signal.decimals = 0;
      }
    } else {
      this.signal.scale = 0;
      this.signal.unit = '';
      this.signal.decimals = 0;
    }

  }

  private createScaleChangeWarningModal(triggerList: any) {
    const removeMessageTitle = this.translate.instant('Scale change');
    const removeMessageBody = this.translate.instant('This signal is used in the "%s" trigger configurations. Please adjust condition after updating the scale').replace('%s', triggerList);
    const removeMessageOK = this.translate.instant('Ok');
    const removeMessageCancel = this.translate.instant('Cancel');

    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = removeMessageTitle;
    modalRef.componentInstance.body = removeMessageBody;
    modalRef.componentInstance.cancelButtonText = removeMessageCancel;
    modalRef.componentInstance.okButtonText = removeMessageOK;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        return;
      }
      this.updateSignalProperties();
      this.activeModal.close(this.signal);
    });
  }

  private shouldShowScaleChangingWarning() {
    return this.signal.scale !== this.originalSignalScale && (this.signal.usedInTriggers && this.signal.usedInTriggers.length > 0);
  }

  private setTranslatedTypes() {
    for (const element of this.signalTypes) {
      this.translatedSignalTypes = [...this.translatedSignalTypes, {
        key: element,
        value: this.translate.instant(element)
      }];
    }
  }

  private setDisabledStatusForInput(signalType: string, inputId: string) {
    if (signalType === 'BOOL' || signalType === 'STRING') {
      this.signalForm.get(inputId)?.disable();
    } else {
      this.signalForm.get(inputId)?.enable();
    }
  }
}
